import { Component, inject, input, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgencyMiniDto, CreatedResponseDto, InternalUserCreateDto, InternalUsersClient, RolesClient } from '@unity/clients';
import { DropdownAgencyControlComponent } from '@unity/forms/dropdown-agency-control/dropdown-agency-control.component';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { AhfcDomain } from '@unity/utils/constants';
import { nonZeroFormControl, noSpecialCharactersRegex, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class UserCreationSchema implements FormGroupControlSchema<InternalUserCreateDto> {
  public user_name = stringFormControl({ validators: [Validators.required, Validators.pattern(noSpecialCharactersRegex)] });
  public agency_id = nonZeroFormControl();
  public role_id = nonZeroFormControl();

  public constructor(agency_id?: number) {
    this.agency_id = nonZeroFormControl({ value: agency_id });
  }
}

@Component({
  selector: 'unity-new-user-internal-page',
  standalone: true,
  imports: [
    KENDO_BUTTON,
    KENDO_LOADER,
    KENDO_CARD,
    ReactiveFormsModule,
    DropdownAgencyControlComponent,
    DropdownControlComponent,
    TextboxControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-user-internal-page.component.html',
  styleUrl: './new-user-internal-page.component.scss',
})
export class NewUserInternalPageComponent extends AbstractNewComponent<InternalUserCreateDto, UserCreationSchema, CreatedResponseDto> {
  // Dependencies
  private readonly InternalUsersClient = inject(InternalUsersClient);
  private readonly rolesClient = inject(RolesClient);

  // Inputs
  public readonly $agencyId = input.required({ alias: 'agencyId', transform: (value: string) => parseInt(value) });

  // Impl
  protected override readonly $contentHeader = signal('Create New Internal User');
  protected override createResourceFn$ = (form: FormGroup<UserCreationSchema>) => {
    const formData = form.getRawValue();
    formData.user_name = formData.user_name + AhfcDomain;
    return this.InternalUsersClient.createOne(formData);
  };
  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => ['/user', response.id];
  protected override newControlsFn(): UserCreationSchema {
    const agencyId = this.$agencyId();
    return new UserCreationSchema(agencyId);
  }

  protected suffix = AhfcDomain;

  protected readonly $selectedAgencyId = linkedSignal(() => {
    return this.$agencyId();
  });

  protected validRolesResource = rxResource({
    params: () => ({ agencyId: this.$selectedAgencyId() }),
    stream: ({ params: { agencyId } }) => this.rolesClient.getAllAsDetailed(agencyId),
  });

  protected $inputRoles = this.validRolesResource.value;
  protected returnToDashboardAction = QuickActions.ReturnToDashboard;

  protected onAgencySelect(agency: AgencyMiniDto) {
    this.$selectedAgencyId.set(agency.id);
    this.$formGroup().controls.role_id.reset(undefined);
  }
}
